import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/KeycloakContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useCredit } from '../../contexts/CreditContext';
import CreditInsightReport from '../credits/CreditInsightReport';
import Banner from '../../ui/Banner';
import Pagination from '../../ui/Pagination';
import Toast from '../../ui/Toast';
import './CreditManagement.css';
import '../workspace/Members.css';

const CreditManagement = () => {
  const { keycloak } = useAuth();
  const { workspace, canManageMembers, refreshWorkspaceContext } = useWorkspace();
  const { refreshBalance } = useCredit();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL query param first
    const urlTab = searchParams.get('tab');
    if (urlTab && ['history', 'insights', 'transfers', 'assign'].includes(urlTab)) {
      return urlTab;
    }
    // Then check sessionStorage
    try {
      const saved = sessionStorage.getItem('cm_transfer_success');
      if (saved) {
        const parsed = JSON.parse(saved);
        sessionStorage.removeItem('cm_transfer_success');
        if (parsed.message) {
          setTimeout(() => setTransferSuccess(parsed.message), 50);
        }
        return parsed.tab || 'history';
      }
    } catch (e) {
      /* ignore */
    }
    return 'history';
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date Helper for Usage Insights
  const getPresetDates = (preset) => {
    const now = new Date();
    let start = new Date();
    let end = new Date();
    const formatLocalPart = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    switch (preset) {
      case 'This Week': {
        const day = now.getDay();
        const diffToMonday = day === 0 ? 6 : day - 1;
        start = new Date(now);
        start.setDate(now.getDate() - diffToMonday);
        end = new Date(start);
        end.setDate(start.getDate() + 4);
        break;
      }
      case 'This Month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'This Year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return null;
    }

    return {
      startDate: formatLocalPart(start),
      endDate: formatLocalPart(end),
    };
  };

  const [insightDate, setInsightDate] = useState(getPresetDates('This Week'));
  const [activePreset, setActivePreset] = useState('This Week');
  const [insightGroup, setInsightGroup] = useState('day');

  const applyInsightPreset = (p) => {
    const range = getPresetDates(p);
    if (range) {
      setInsightDate(range);
      setActivePreset(p);
    }
  };

  // Data states
  const [members, setMembers] = useState([]);
  const [history, setHistory] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, total: 0, limit: 10 });

  // Handle search query parameter for assign tab
  useEffect(() => {
    if (activeTab === 'assign') {
      const search = searchParams.get('search');
      if (search) {
        // Pre-fill the search bar with the member name
        setAssignSearch(search);
        if (members.length > 0) {
          const matchedMember = members.find(
            (m) => m.display_name && m.display_name.toLowerCase().includes(search.toLowerCase())
          );
          if (matchedMember) {
            setTransferForm((prev) => ({ ...prev, toUserId: matchedMember.user_id }));
          }
        }
      }
    }
  }, [activeTab, searchParams, members]);

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    toUserId: '',
    amount: '',
    description: '',
    type: 'assign',
  });
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(null);
  const [activeTransferMember, setActiveTransferMember] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const clearTransferSuccess = useCallback(() => setTransferSuccess(null), []);

  // History filters
  const [historyFilters, setHistoryFilters] = useState({
    username: '',
    amountType: 'all', // 'all', 'increase', 'decrease'
    description: '',
  });

  // Filtered members for assign tab
  const filteredAssignMembers = members.filter(
    (m) =>
      m.role !== 'org_owner' &&
      m.role !== 'org_admin' &&
      m.role !== 'owner' &&
      m.role !== 'admin' &&
      (assignSearch === '' ||
        (m.display_name && m.display_name.toLowerCase().includes(assignSearch.toLowerCase())))
  );

  // Filtered history
  const filteredHistory = history.filter((item) => {
    const matchesUsername =
      !historyFilters.username ||
      (item.display_name && item.display_name.toLowerCase().includes(historyFilters.username.toLowerCase()));
    const matchesAmountType =
      historyFilters.amountType === 'all' ||
      (historyFilters.amountType === 'increase' && item.amount > 0) ||
      (historyFilters.amountType === 'decrease' && item.amount < 0);
    const matchesDescription =
      !historyFilters.description ||
      (item.description && item.description.toLowerCase().includes(historyFilters.description.toLowerCase()));
    return matchesUsername && matchesAmountType && matchesDescription;
  });

  const closeTransferModal = useCallback(() => {
    setActiveTransferMember(null);
    setTransferForm({ toUserId: '', amount: '', description: '', type: 'assign' });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const config = {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      };

      if (activeTab === 'history') {
        const res = await axios.get(
          `/credits/org/history?page=${pagination.page}&limit=${pagination.limit}`,
          config
        );
        setHistory(res.data.data);
        setPagination((prev) => ({ ...prev, total: res.data.total }));
      } else if (activeTab === 'transfers') {
        const res = await axios.get(
          `/credits/org/transfers?page=${pagination.page}&limit=${pagination.limit}`,
          config
        );
        setTransfers(res.data.data);
        setPagination((prev) => ({ ...prev, total: res.data.total }));
      } else if (activeTab === 'assign') {
        // Fetch members for the assign credits dropdown
        const res = await axios.get('/credits/org/members/status', config);
        setMembers(res.data.data);
      }
    } catch (err) {
      console.error('[CreditManagement] Fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load credit data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, keycloak.token, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTransferSubmit = async (e) => {
    if (e) e.preventDefault();
    setTransferLoading(true);
    setTransferSuccess(null);
    setError(null);

    try {
      const config = {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      };

      let response;
      let msg;

      if (transferForm.type === 'assign') {
        response = await axios.post(
          '/credits/org/transfer',
          {
            toUserId: transferForm.toUserId,
            amount: parseInt(transferForm.amount, 10),
            description: transferForm.description,
          },
          config
        );
        msg = `Successfully assigned ${transferForm.amount} credits.`;
      } else {
        // retrieve
        response = await axios.post(
          '/credits/org/retrieve',
          {
            fromMemberId: transferForm.toUserId,
            amount: parseInt(transferForm.amount, 10),
          },
          config
        );
        msg = `Successfully retrieved ${transferForm.amount} credits.`;
      }

      const updatedBalance = Number(
        response?.data?.data?.toBalance || response?.data?.data?.memberBalance
      );
      if (Number.isFinite(updatedBalance) && activeTransferMember) {
        setMembers((prev) =>
          prev.map((m) =>
            m.user_id === activeTransferMember.user_id
              ? { ...m, credit_balance: Math.max(0, updatedBalance) }
              : m
          )
        );
      }

      setTransferForm({ toUserId: '', amount: '', description: '', type: 'assign' });
      setTransferSuccess(msg);
      setActiveTab('history');
      closeTransferModal();
      try {
        sessionStorage.setItem(
          'cm_transfer_success',
          JSON.stringify({ message: msg, tab: 'history' })
        );
      } catch (e) {
        /* ignore */
      }
      // Direct DOM banner: survives any React remount/re-render
      try {
        const existing = document.getElementById('org-transfer-toast');
        if (existing) existing.remove();
        const banner = document.createElement('div');
        banner.id = 'org-transfer-toast';
        banner.innerHTML = `<i class="fas fa-check-circle"></i> ${msg}`;
        banner.style.cssText =
          'position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:99999;background:#d4edda;color:#155724;border:1px solid #c3e6cb;border-left:4px solid #38a169;border-radius:12px;padding:16px 24px;font-size:14px;font-weight:500;box-shadow:0 10px 25px -5px rgba(56,161,105,0.15);display:flex;align-items:center;gap:12px;animation:orgToastSlide 0.35s cubic-bezier(0.16,1,0.3,1);pointer-events:auto;';
        document.body.appendChild(banner);
        const styleEl = document.createElement('style');
        styleEl.textContent =
          '@keyframes orgToastSlide {from{opacity:0;transform:translateX(-50%) translateY(-20px);}to{opacity:1;transform:translateX(-50%) translateY(0);}} #org-transfer-toast + .org-toast-close{display:none;}';
        document.head.appendChild(styleEl);
        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '<i class="fas fa-times"></i>';
        closeBtn.style.cssText =
          'background:none;border:none;color:#155724;cursor:pointer;padding:6px;opacity:0.7;';
        closeBtn.onmouseenter = () => (closeBtn.style.opacity = '1');
        closeBtn.onmouseleave = () => (closeBtn.style.opacity = '0.7');
        closeBtn.onclick = () => banner.remove();
        banner.appendChild(closeBtn);
        setTimeout(() => {
          if (banner.parentNode) banner.remove();
        }, 6000);
      } catch (e) {
        /* ignore */
      }
      // Refresh member list so the updated balance shows
      fetchData();
      // Refresh workspace context to update sidebar credits
      if (refreshWorkspaceContext) refreshWorkspaceContext();
      // Refresh live credit balance (updates sidebar instantly via CreditContext)
      if (refreshBalance) {
        refreshBalance();
      }
      // Dispatch custom event to refresh profile in App.js
      window.dispatchEvent(new CustomEvent('refresh-profile'));
    } catch (err) {
      console.error('[CreditManagement] Transfer failed:', err);
      setError(err.response?.data?.error || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const renderHistory = () => (
    <div className="credit-table-card">
      {/* Filter Bar */}
      <div
        className="filter-bar"
        style={{
          padding: '16px 24px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>User</label>
          <input
            type="text"
            placeholder="Filter by name..."
            value={historyFilters.username}
            onChange={(e) => setHistoryFilters((prev) => ({ ...prev, username: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#fff',
              outline: 'none',
            }}
          />
        </div>
        <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Amount Type</label>
          <select
            value={historyFilters.amountType}
            onChange={(e) => setHistoryFilters((prev) => ({ ...prev, amountType: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#fff',
              outline: 'none',
            }}
          >
            <option value="all">All</option>
            <option value="increase">Increase (+)</option>
            <option value="decrease">Decrease (-)</option>
          </select>
        </div>
        <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Description</label>
          <input
            type="text"
            placeholder="Filter by description..."
            value={historyFilters.description}
            onChange={(e) => setHistoryFilters((prev) => ({ ...prev, description: e.target.value }))}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#fff',
              outline: 'none',
            }}
          />
        </div>
        {(historyFilters.username || historyFilters.amountType !== 'all' || historyFilters.description) && (
          <button
            onClick={() => setHistoryFilters({ username: '', amountType: 'all', description: '' })}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #ffdada',
              color: '#ff4d4d',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              height: '38px',
              marginTop: 'auto',
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-scroll-container">
        <table className="credit-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>User</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Provider</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.map((item) => (
              <tr key={item.id}>
                <td style={{ color: '#666', fontSize: '12px' }}>
                  {new Date(item.created_at).toLocaleString()}
                </td>
                <td>{item.display_name}</td>
                <td>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: item.amount < 0 ? '#dc3545' : '#28a745',
                    }}
                  >
                    {item.transaction_type}
                  </span>
                </td>
                <td style={{ fontWeight: '700' }}>
                  {item.amount > 0 ? '+' : ''}
                  {item.amount.toLocaleString()}
                </td>
                <td>
                  <span className="status-badge" style={{ background: '#eee', color: '#555' }}>
                    {item.provider}
                  </span>
                </td>
                <td style={{ fontSize: '13px', color: '#666' }}>{item.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredHistory.length === 0 && !loading && (
        <div className="empty-state">
          <i className="fas fa-history"></i>
          <p>No usage history found.</p>
        </div>
      )}
      <Pagination
        currentPage={pagination.page + 1}
        totalPages={Math.ceil(pagination.total / pagination.limit)}
        onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p - 1 }))}
        itemsPerPage={pagination.limit}
        onItemsPerPageChange={(limit) => setPagination((prev) => ({ ...prev, limit, page: 0 }))}
        totalItems={pagination.total}
        itemsName="records"
      />
    </div>
  );

  const renderTransfers = () => {
    return (
      <div className="credit-table-card">
        <div className="table-scroll-container">
          <table className="credit-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((item) => {
                const amount = Number(item.amount) || 0;
                const isAssign = item.type === 'assignment';
                const typeLabel = isAssign ? 'Assignment' : 'Retrieval';
                const amountPrefix = isAssign ? '+' : '-';
                return (
                  <tr key={item.id}>
                    <td style={{ color: '#666', fontSize: '12px' }}>
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                    <td>{item.from_name}</td>
                    <td>{item.to_name}</td>
                    <td>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          color: isAssign ? '#3b82f6' : '#dc3545',
                        }}
                      >
                        {typeLabel}
                      </span>
                    </td>
                    <td style={{ fontWeight: '700', ...(isAssign ? { color: '#10b981' } : { color: '#ef4444' }) }}>
                      {amountPrefix}{Math.abs(amount).toLocaleString()}
                    </td>
                    <td style={{ fontSize: '13px', color: '#666' }}>{item.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {transfers.length === 0 && !loading && (
          <div className="empty-state">
            <i className="fas fa-exchange-alt"></i>
            <p>No assignment history found.</p>
          </div>
        )}
      </div>
    );
  };

  const renderAssign = () => (
    <div className="credit-table-card" style={{ padding: '24px' }}>
      <h3 style={{ marginBottom: '24px', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
        Assign Credits to Members
      </h3>

      {/* Search Bar */}
      <div
        className="filter-bar"
        style={{
          marginBottom: '24px',
          padding: '20px 24px',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div className="tm-search" style={{ maxWidth: '400px', height: '48px', position: 'relative' }}>
          <i className="fas fa-search" style={{ 
            fontSize: '16px', 
            color: '#94a3b8',
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
          }}></i>
          <input
            type="text"
            placeholder="Search members by name..."
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            style={{
              width: '100%',
              height: '100%',
              fontSize: '15px',
              paddingLeft: '44px',
              paddingRight: '16px',
              border: 'none',
              borderRadius: '10px',
              background: '#fff',
              outline: 'none',
              textAlign: 'center',
            }}
          />
        </div>
      </div>

      {/* Members Table */}
      <div
        className="table-scroll-container"
        style={{
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 400px)',
        }}
      >
        <table className="credit-table" style={{ width: '100%' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#64748b',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                Member
              </th>
              <th
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#64748b',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                Role
              </th>
              <th
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#64748b',
                  borderBottom: '1px solid #e2e8f0',
                  width: '180px',
                }}
              >
                Credit Balance
              </th>
              <th
                style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '13px',
                  color: '#64748b',
                  borderBottom: '1px solid #e2e8f0',
                  width: '240px',
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAssignMembers.map((member) => (
              <tr key={member.user_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '16px 20px', verticalAlign: 'middle' }}>
                  <div
                    className="user-cell"
                    style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                  >
                    <div
                      className="user-avatar"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        flexShrink: 0,
                      }}
                    >
                      {String(member.display_name || '?')
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div>
                      <div
                        className="user-name-bold"
                        style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}
                      >
                        {member.display_name ? String(member.display_name) : 'Unknown Member'}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '16px 20px', verticalAlign: 'middle' }}>
                  <span
                    style={{
                      textTransform: 'capitalize',
                      fontSize: '13.5px',
                      color: '#475569',
                      fontWeight: '500',
                    }}
                  >
                    {member.role.replace('org_', '')}
                  </span>
                </td>
                <td style={{ padding: '16px 20px', verticalAlign: 'middle' }}>
                  <strong style={{ fontSize: '14.5px', color: '#1e293b' }}>
                    {member.credit_balance.toLocaleString()}
                  </strong>
                </td>
                <td style={{ padding: '16px 20px', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="page-btn"
                      style={{
                        flex: 1,
                        height: '40px',
                        fontSize: '13.5px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => (e.target.style.transform = 'translateY(-1px)')}
                      onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)')}
                      onClick={() => {
                        setTransferForm((prev) => ({
                          ...prev,
                          toUserId: member.user_id,
                          type: 'assign',
                        }));
                        setActiveTransferMember(member);
                      }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '6px' }}></i> Assign
                    </button>
                    <button
                      className="page-btn"
                      style={{
                        flex: 1,
                        height: '40px',
                        fontSize: '13.5px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        background: '#fff',
                        color: '#dc3545',
                        border: '1.5px solid #dc3545',
                        cursor: member.credit_balance <= 0 ? 'not-allowed' : 'pointer',
                        opacity: member.credit_balance <= 0 ? 0.5 : 1,
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (member.credit_balance > 0) e.target.style.background = '#fff5f5';
                      }}
                      onMouseLeave={(e) => {
                        if (member.credit_balance > 0) e.target.style.background = '#fff';
                      }}
                      onClick={() => {
                        setTransferForm((prev) => ({
                          ...prev,
                          toUserId: member.user_id,
                          type: 'retrieve',
                        }));
                        setActiveTransferMember(member);
                      }}
                      disabled={member.credit_balance <= 0}
                    >
                      <i className="fas fa-minus" style={{ marginRight: '6px' }}></i> Retrieve
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAssignMembers.length === 0 && !loading && (
          <div className="empty-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <i
              className="fas fa-users"
              style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }}
            ></i>
            <p style={{ fontSize: '15px', color: '#94a3b8', margin: 0 }}>No members found.</p>
          </div>
        )}
      </div>

      {/* Transfer Modal */}
      {activeTransferMember && (
        <div
          className="tm-overlay"
          onClick={(e) => e.target === e.currentTarget && closeTransferModal()}
          style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="tm-modal animate-fade-in"
            style={{
              maxWidth: '480px',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              className="tm-modal-head"
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                borderRadius: '16px 16px 0 0',
                background: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: '700',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <i
                  className={`fas fa-${transferForm.type === 'assign' ? 'arrow-up' : 'arrow-down'}`}
                  style={{
                    color: transferForm.type === 'assign' ? '#3b82f6' : '#dc3545',
                    fontSize: '16px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: transferForm.type === 'assign' ? '#dbeafe' : '#fff5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                ></i>
                {transferForm.type === 'assign' ? 'Assign Credits' : 'Retrieve Credits'}
              </h3>
              <button
                className="tm-modal-close"
                onClick={closeTransferModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '18px',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="tm-modal-body" style={{ padding: '24px' }}>
              <p
                style={{
                  fontSize: '14.5px',
                  color: '#64748b',
                  marginTop: 0,
                  marginBottom: '24px',
                  lineHeight: '1.6',
                }}
              >
                {transferForm.type === 'assign' ? (
                  <>
                    Assign credits to{' '}
                    <strong style={{ color: '#1e293b' }}>{activeTransferMember.display_name}</strong>
                    {' '}
                    . This will be deducted from your organization pool.
                  </>
                ) : (
                  <>
                    Retrieve credits from{' '}
                    <strong style={{ color: '#1e293b' }}>{activeTransferMember.display_name}</strong>
                    {' '}
                    back to your organization pool.
                  </>
                )}
              </p>

              <div className="tm-field" style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                  }}
                >
                  Amount to {transferForm.type === 'assign' ? 'Assign' : 'Retrieve'}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={transferForm.amount}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '');
                    setTransferForm((prev) => ({ ...prev, amount: digitsOnly }));
                  }}
                  placeholder="Enter credit amount"
                  min="1"
                  autoFocus
                  style={{
                    width: '100%',
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: '600',
                    padding: '0 16px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#fff',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <p
                  className="tm-field-hint"
                  style={{ marginTop: '8px', fontSize: '13px', color: '#94a3b8' }}
                >
                  Current member balance:{' '}
                  <strong style={{ color: '#1e293b', fontSize: '14px' }}>
                    {(activeTransferMember.credit_balance || 0).toLocaleString()}
                  </strong>
                </p>
              </div>

              <div className="tm-field" style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                  }}
                >
                  Reason / Note (Optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Monthly top-up"
                  value={transferForm.description}
                  onChange={(e) =>
                    setTransferForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    height: '48px',
                    fontSize: '15px',
                    padding: '0 16px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '10px',
                    background: '#fff',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div
              className="tm-modal-foot"
              style={{
                padding: '20px 24px',
                borderTop: '1px solid #e2e8f0',
                borderRadius: '0 0 16px 16px',
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                type="button"
                className="tm-btn tm-btn-outline"
                onClick={closeTransferModal}
                style={{
                  height: '42px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  padding: '0 20px',
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  background: '#fff',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tm-btn tm-btn-primary"
                disabled={
                  transferLoading ||
                  !transferForm.amount ||
                  (transferForm.type === 'retrieve' &&
                    Number(transferForm.amount) > (activeTransferMember.credit_balance || 0))
                }
                onClick={handleTransferSubmit}
                style={{
                  minWidth: '160px',
                  height: '42px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  background:
                    transferForm.type === 'assign'
                      ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                      : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                  border: 'none',
                  color: '#fff',
                  cursor: transferLoading || !transferForm.amount ? 'not-allowed' : 'pointer',
                  opacity: transferLoading || !transferForm.amount ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                {transferLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      className="tm-spinner"
                      style={{ width: '16px', height: '16px', borderWidth: '2px' }}
                    ></div>
                    Processing...
                  </span>
                ) : transferForm.type === 'assign' ? (
                  'Confirm Assignment'
                ) : (
                  'Confirm Retrieval'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="tm-page">
      <Banner
        title="Credit Management"
        subtitle="Manage member credit allocations, monitor usage, and view transaction history for your organization."
        type="page-header"
      />
      <Toast message={transferSuccess} type="success" onClose={clearTransferSuccess} />

      {transferSuccess && (
        <div
          className="status-badge available"
          style={{
            width: '100%',
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
          }}
        >
          <i className="fas fa-check-circle"></i> {transferSuccess}
        </div>
      )}

      {error && (
        <div
          className="status-badge reached_limit"
          style={{ width: '100%', padding: '12px', marginBottom: '20px', borderRadius: '8px' }}
        >
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      <div className="tm-section-tabs">
        <button
          className={`tm-section-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('history');
            navigate('?tab=history', { replace: true });
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        >
          <i className="fas fa-history"></i> Usage History
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('insights');
            navigate('?tab=insights', { replace: true });
          }}
        >
          <i className="fas fa-chart-line"></i> Usage Insights
        </button>
        <div className="tm-tab-divider"></div>
        <button
          className={`tm-section-tab ${activeTab === 'assign' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('assign');
            navigate('?tab=assign', { replace: true });
          }}
        >
          <i className="fas fa-plus-circle"></i> Assign Credits
        </button>
      </div>

      {/* Usage Insights Tab */}
      {activeTab === 'insights' && (
        <div
          className="tm-filter-bar animate-fade-in"
          style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <div
            className="tm-tabs"
            style={{
              display: 'flex',
              gap: '4px',
              background: '#f1f5f9',
              padding: '4px',
              borderRadius: '8px',
            }}
          >
            {['This Week', 'This Month', 'This Year'].map((p) => (
              <button
                key={p}
                className={`tm-tab-pill ${activePreset === p ? 'active' : ''}`}
                onClick={() => applyInsightPreset(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>VIEW BY</span>
              <select
                className="tm-select sm"
                value={insightGroup || 'day'}
                onChange={(e) => setInsightGroup(e.target.value)}
                style={{
                  height: '36px',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '0 12px',
                  borderRadius: '8px',
                }}
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>

            <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={insightDate.startDate}
                onChange={(e) => {
                  setInsightDate((prev) => ({ ...prev, startDate: e.target.value }));
                  setActivePreset('Custom');
                }}
              />
            </div>
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700' }}>TO</span>
            <div className="tm-search" style={{ height: '36px', minWidth: '150px' }}>
              <i className="fas fa-calendar-alt"></i>
              <input
                type="date"
                value={insightDate.endDate}
                onChange={(e) => {
                  setInsightDate((prev) => ({ ...prev, endDate: e.target.value }));
                  setActivePreset('Custom');
                }}
              />
            </div>
          </div>
        </div>
      )}
      {activeTab === 'insights' && (
        <div className="animate-fade-in">
          <CreditInsightReport
            isSuperadmin={false}
            dateRange={insightDate}
            groupBy={insightGroup}
          />
        </div>
      )}

      {loading ? (
        <div className="loading-row">
          <div className="spinner"></div>
          <p>Loading credit data...</p>
        </div>
      ) : (
        <>
          {activeTab === 'history' && renderHistory()}
          {activeTab === 'transfers' && renderTransfers()}
          {activeTab === 'assign' && renderAssign()}
        </>
      )}
    </div>
  );
};

export default CreditManagement;
